/**
 * PackWorkspace + PptxPanel + CopyButton + EmailPanel + IntranetPanel
 * — the 4-artefact pack viewer with download buttons.
 *
 * Extracted from App.js during P1 Phase-3 refactor. Zero behavioural
 * changes.
 */
import React, { useState } from "react";

import { downloadFromPath, logDev, OUTPUT_TABS } from "../lib/beaconUtils";

export function PackWorkspace({ pack, generating, onClear }) {
  const [activeTab, setActiveTab] = useState("poster");

  if (generating && !pack) {
    return (
      <section className="beacon-workspace" data-testid="output-workspace">
        <div className="beacon-tab-loading" data-testid="pack-loading">
          <span className="beacon-spinner beacon-spinner-dark" />
          Beacon is reading the source, deriving the topic, and writing four
          aligned formats. ~30–60s on live Claude.
        </div>
      </section>
    );
  }
  if (!pack) return null;

  return (
    <section className="beacon-workspace" data-testid="output-workspace">
      <div className="beacon-pack-header" data-testid="pack-header">
        <div>
          <div className="beacon-tiny-label">CURRENT PACK</div>
          <div className="beacon-pack-title" data-testid="pack-title">
            {pack.topic.topic_name}
          </div>
          <div className="beacon-pack-theme">
            Theme: {pack.topic.theme_name}
          </div>
          {(pack.topic.codes_of_practice || []).length > 0 && (
            <div className="beacon-pack-cop" data-testid="pack-cop-tag"
              title="Safe Work Australia model Code(s) of Practice this pack's guidance is grounded in">
              Grounded in: {pack.topic.codes_of_practice.map(
                (n) => `Code of Practice — ${n}`,
              ).join(" · ")}
            </div>
          )}
        </div>
        <div className="beacon-pack-header-side">
          {pack.source_manifest && (
            <div className="beacon-pack-manifest" data-testid="pack-manifest">
              <div className="beacon-tiny-label">SOURCED FROM</div>
              {pack.source_manifest.map((m, i) => (
                <div key={`${m.name}-${i}`} className="beacon-pack-manifest-line">
                  <span>{m.name}</span>
                  {m.char_count >= 0 && <code>{m.char_count} chars</code>}
                </div>
              ))}
            </div>
          )}
          {onClear && (
            <button
              type="button"
              className="beacon-secondary-btn beacon-pack-clear-btn"
              data-testid="clear-pack"
              onClick={onClear}
            >
              Clear &amp; start over
            </button>
          )}
        </div>
      </div>

      <div className="beacon-tabs" role="tablist">
        {OUTPUT_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            data-testid={`tab-${tab.key}`}
            className={`beacon-tab${activeTab === tab.key ? " is-active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <span className="beacon-tab-label">{tab.label}</span>
            <span className="beacon-tab-sub">{tab.sub}</span>
            <span className="beacon-status-dot is-done" />
          </button>
        ))}
      </div>

      <div className="beacon-tab-panel" data-testid="active-tab-panel">
        {activeTab === "toolbox" && (
          <PptxPanel data={pack.toolbox} label="Toolbox Handout" testKey="toolbox" />
        )}
        {activeTab === "poster" && (
          <PptxPanel data={pack.poster} label="Theme Poster" testKey="poster" />
        )}
        {activeTab === "email" && <EmailPanel data={pack.email} />}
        {activeTab === "intranet" && <IntranetPanel data={pack.intranet} />}
      </div>
    </section>
  );
}

function PptxPanel({ data, label, testKey }) {
  return (
    <div className="beacon-pptx-panel" data-testid={`pptx-panel-${testKey}`}>
      <div className="beacon-pptx-meta">
        <div className="beacon-pptx-title">{label} · PPTX ready</div>
        <div className="beacon-pptx-filename">{data.filename}</div>
        <div className="beacon-pptx-stats">
          <span>Model: <strong>{data.model_id}</strong></span>
        </div>
      </div>
      <button
        type="button"
        className="beacon-secondary-btn"
        data-testid={`download-${testKey}`}
        onClick={() => downloadFromPath(data.download_path, data.filename)}
      >Download PPTX</button>
      <p className="beacon-pptx-note">
        PowerPoint files are downloaded directly — preview them in PowerPoint
        or Keynote. Layout is locked; only content text was AI-generated.
      </p>
    </div>
  );
}

function CopyButton({ label, getPayload, testid }) {
  const [state, setState] = useState("idle");
  const handleClick = async () => {
    try {
      const payload = getPayload();
      if (payload.kind === "rich") {
        const htmlBlob = new Blob([payload.html], { type: "text/html" });
        const textBlob = new Blob([payload.plain], { type: "text/plain" });
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "text/html": htmlBlob,
            "text/plain": textBlob,
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(payload.text);
      }
      setState("copied");
      setTimeout(() => setState("idle"), 1600);
    } catch (err) {
      logDev("Clipboard write failed", err);
      setState("error");
      setTimeout(() => setState("idle"), 2200);
    }
  };
  return (
    <button
      type="button"
      data-testid={testid}
      className={`beacon-copy-btn is-${state}`}
      onClick={handleClick}
    >{state === "copied" ? "Copied ✓" : state === "error" ? "Copy blocked" : label}</button>
  );
}

function EmailPanel({ data }) {
  return (
    <div className="beacon-email-panel" data-testid="email-panel">
      <div className="beacon-email-subject">
        <span className="beacon-tiny-label">SUBJECT</span>
        <div className="beacon-email-subject-value" data-testid="email-subject">
          {data.subject_line}
        </div>
        <CopyButton
          label="Copy subject"
          testid="copy-email-subject"
          getPayload={() => ({ kind: "text", text: data.subject_line })}
        />
      </div>
      <div className="beacon-copy-bar">
        <CopyButton label="Copy as Rich Text" testid="copy-email-rich"
          getPayload={() => ({ kind: "rich", html: data.html, plain: data.plain_text })} />
        <CopyButton label="Copy HTML" testid="copy-email-html"
          getPayload={() => ({ kind: "text", text: data.html })} />
        <CopyButton label="Copy Plain Text" testid="copy-email-plain"
          getPayload={() => ({ kind: "text", text: data.plain_text })} />
        <CopyButton label="Copy Markdown" testid="copy-email-markdown"
          getPayload={() => ({ kind: "text", text: data.markdown })} />
      </div>
      <div className="beacon-preview-frame" data-testid="email-preview">
        <iframe title="Email preview"
          srcDoc={`<html><body style="margin:0;padding:24px;background:#fff;">${data.html}</body></html>`}
          className="beacon-preview-iframe" />
      </div>
    </div>
  );
}

function IntranetPanel({ data }) {
  return (
    <div className="beacon-email-panel" data-testid="intranet-panel">
      <div className="beacon-email-subject">
        <span className="beacon-tiny-label">ARTICLE TITLE</span>
        <div className="beacon-email-subject-value" data-testid="intranet-title">
          {data.title}
        </div>
      </div>
      {data.tags?.length > 0 && (
        <div className="beacon-tag-row" data-testid="intranet-tags">
          {data.tags.map((t) => <code key={t} className="beacon-tag-pill">{t}</code>)}
        </div>
      )}
      <div className="beacon-copy-bar">
        <CopyButton label="Copy as Rich Text" testid="copy-intranet-rich"
          getPayload={() => ({ kind: "rich", html: data.html, plain: data.plain_text })} />
        <CopyButton label="Copy HTML" testid="copy-intranet-html"
          getPayload={() => ({ kind: "text", text: data.html })} />
        <CopyButton label="Copy Markdown" testid="copy-intranet-markdown"
          getPayload={() => ({ kind: "text", text: data.markdown })} />
        <CopyButton label="Copy Plain Text" testid="copy-intranet-plain"
          getPayload={() => ({ kind: "text", text: data.plain_text })} />
      </div>
      <div className="beacon-preview-frame" data-testid="intranet-preview">
        <iframe title="Intranet preview"
          srcDoc={`<html><body style="margin:0;padding:24px;background:#fff;">${data.html}</body></html>`}
          className="beacon-preview-iframe beacon-preview-iframe-tall" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Annual Plan View — extracted to ./views/AnnualPlanView.jsx during
// the P1 Phase-2 refactor.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Programs View — extracted to ./views/ProgramsView.jsx during the P1
// Phase-2 refactor.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Library View — extracted to ./views/LibraryView.jsx during the P1 refactor.
// ---------------------------------------------------------------------------
